import { useQuery } from "@tanstack/react-query";
import { BookOpen, Check, Plus, Search } from "lucide-react";
import { type ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { api } from "../../lib/api";
import { cn } from "../../lib/utils";
import type { Notebook } from "../../types/api";
import { type AddToNotebookInput, useAddToNotebook } from "./useAddToNotebook";

interface Props {
  item: AddToNotebookInput;
  trigger: ReactElement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onAdded?: (info: { notebookId: string; notebookTitle: string }) => void;
}

/**
 * AddToNotebookPopover — Readwise-style picker.
 *
 * Layout: header → "Último" with a primary action → search input → recent
 * notebooks list (5 most recent) → "Crear nuevo" inline create row.
 *
 * Keyboard: Enter confirms the focused option (last by default), ↑/↓ navigate,
 * Esc closes (handled by Radix Popover).
 */
export function AddToNotebookPopover({ item, trigger, open, onOpenChange, onAdded }: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setOpen = (next: boolean) => {
    if (open === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  };

  const { addToLast, addToNotebook, createAndAdd, lastNotebookId } = useAddToNotebook();
  const notebooksQuery = useQuery({
    queryKey: ["notebooks"],
    queryFn: () => api.listNotebooks(),
    enabled: isOpen,
  });
  const [query, setQuery] = useState("");
  const [newName, setNewName] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const notebooks = notebooksQuery.data?.items ?? [];
  const last = useMemo<Notebook | null>(() => {
    if (!lastNotebookId) return null;
    return notebooks.find((n) => n.id === lastNotebookId) ?? null;
  }, [notebooks, lastNotebookId]);
  const filtered = useMemo(
    () => filterNotebooks(notebooks, query, last?.id ?? null),
    [notebooks, query, last],
  );

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setNewName("");
      setActiveIndex(0);
      return;
    }
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  function close(result?: { notebookId: string; notebookTitle: string }): void {
    if (result) onAdded?.(result);
    setOpen(false);
  }

  function handleAddToLast(): void {
    addToLast.mutate(item, {
      onSuccess: (result) => {
        toast.success("Añadido a apuntes", { description: result.notebookTitle });
        close({ notebookId: result.notebookId, notebookTitle: result.notebookTitle });
      },
      onError: (err) => toast.error(`No se pudo añadir: ${err}`),
    });
  }

  function handleAddToNotebook(notebookId: string): void {
    addToNotebook.mutate(
      { notebookId, item },
      {
        onSuccess: (result) => {
          toast.success("Añadido a apuntes", { description: result.notebookTitle });
          close({ notebookId: result.notebookId, notebookTitle: result.notebookTitle });
        },
        onError: (err) => toast.error(`No se pudo añadir: ${err}`),
      },
    );
  }

  function handleCreate(): void {
    const name = newName.trim();
    if (!name) return;
    createAndAdd.mutate(
      { name, item },
      {
        onSuccess: (result) => {
          toast.success("Notebook creado y añadido", { description: result.notebookTitle });
          close({ notebookId: result.notebookId, notebookTitle: result.notebookTitle });
        },
        onError: (err) => toast.error(`No se pudo crear: ${err}`),
      },
    );
  }

  const totalRows = (last ? 1 : 0) + filtered.length + 1; // +1 for "create new"

  function onKeyDown(e: React.KeyboardEvent): void {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, totalRows - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      runActive();
    }
  }

  function runActive(): void {
    let cursor = 0;
    if (last) {
      if (activeIndex === cursor) {
        handleAddToLast();
        return;
      }
      cursor += 1;
    }
    for (const nb of filtered) {
      if (activeIndex === cursor) {
        handleAddToNotebook(nb.id);
        return;
      }
      cursor += 1;
    }
    // "Crear nuevo" row
    if (newName.trim()) handleCreate();
  }

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        className="w-80 p-0"
        data-testid="add-to-notebook-popover"
        onKeyDown={onKeyDown}
      >
        <header className="border-b border-(--border-subtle) px-3 py-2 text-xs font-semibold uppercase tracking-wide text-(--text-tertiary)">
          Añadir a apuntes
        </header>

        {last && (
          <div className="px-2 pt-2">
            <button
              type="button"
              className={cn(
                "flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition",
                activeIndex === 0
                  ? "border-(--accent-500) bg-(--accent-50) text-(--text-primary)"
                  : "border-(--border-subtle) bg-(--bg-base) hover:bg-(--bg-hover)",
              )}
              onMouseEnter={() => setActiveIndex(0)}
              onClick={handleAddToLast}
              disabled={addToLast.isPending}
              data-testid="add-to-notebook-last"
            >
              <Check size={14} className="text-(--accent-500)" />
              <span className="flex-1 truncate">
                <span className="text-[10px] uppercase tracking-wide text-(--text-tertiary)">
                  Último
                </span>
                <span className="block truncate font-medium">{last.title}</span>
              </span>
              <span className="text-[10px] text-(--text-tertiary)">↵</span>
            </button>
          </div>
        )}

        <div className="border-b border-(--border-subtle) px-2 py-2">
          <div className="relative">
            <Search
              size={12}
              className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-(--text-tertiary)"
            />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(last ? 1 : 0);
              }}
              placeholder="Buscar notebook…"
              className="h-8 pl-7 text-xs"
              aria-label="Buscar notebook"
            />
          </div>
        </div>

        <ul className="max-h-60 overflow-y-auto px-1 py-1">
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-xs text-(--text-tertiary)">
              Sin coincidencias. Crea uno nuevo abajo.
            </li>
          )}
          {filtered.map((nb, idx) => {
            const rowIndex = (last ? 1 : 0) + idx;
            return (
              <li key={nb.id}>
                <button
                  type="button"
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm transition",
                    activeIndex === rowIndex
                      ? "bg-(--bg-active) text-(--text-primary)"
                      : "text-(--text-secondary) hover:bg-(--bg-hover)",
                  )}
                  onMouseEnter={() => setActiveIndex(rowIndex)}
                  onClick={() => handleAddToNotebook(nb.id)}
                  disabled={addToNotebook.isPending}
                >
                  <BookOpen size={12} className="text-(--text-tertiary)" />
                  <span className="flex-1 truncate">{nb.title}</span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="border-t border-(--border-subtle) p-2">
          <div className="flex items-center gap-1">
            <Plus size={12} className="text-(--text-tertiary)" />
            <Input
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                setActiveIndex(totalRows - 1);
              }}
              placeholder="Crear nuevo notebook…"
              className="h-8 text-xs"
              aria-label="Nombre del nuevo notebook"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (newName.trim()) handleCreate();
                }
              }}
            />
            <Button
              variant="primary"
              size="sm"
              disabled={!newName.trim() || createAndAdd.isPending}
              onClick={handleCreate}
              aria-label="Crear y añadir"
            >
              <Plus size={12} />
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function filterNotebooks(notebooks: Notebook[], query: string, lastId: string | null): Notebook[] {
  const q = query.trim().toLowerCase();
  const sorted = notebooks
    .filter((n) => n.id !== lastId)
    .sort((a, b) => (a.updated_at > b.updated_at ? -1 : a.updated_at < b.updated_at ? 1 : 0));
  if (!q) return sorted.slice(0, 5);
  return sorted.filter((n) => n.title.toLowerCase().includes(q)).slice(0, 10);
}
